import * as z from 'zod';
import dayjs from 'dayjs';
import { http } from '@noor/api';
import { env } from '@noor/config';
import {
  courierProfileSchema,
  courierOrderRowSchema,
  courierContractSchema,
  courierEquipmentSchema,
  courierBlockSchema,
  courierRatingSchema,
  courierTestResultSchema,
  type CourierProfile,
  type CourierProfileId,
  type CourierOrderRow,
  type CourierContract,
  type CourierEquipment,
  type CourierRequirementField,
  type CourierOrderStatus,
  type CourierPersonalAnswers,
  type CourierPickerOption,
} from '../model/schema';
import type { CourierOrdersSearch } from '../model/orders';

// Matches courier-single in the reference cpanel, including /api/v1.
const profileOptions = { baseURL: `${env.VITE_CORE_API_URL}/api/v1` };
const recordSchema = z.record(z.string(), z.unknown());
const courierSchema = z.object({ id: z.union([z.string(), z.number()]) }).loose();

function text(value: unknown): string {
  return typeof value === 'string' || typeof value === 'number' ? String(value) : '';
}

function numeric(value: unknown): number {
  const parsed = Number(value);
  return Number.isFinite(parsed) ? parsed : 0;
}

function mapPoint(value: unknown): { lat: number; long: number } | null {
  const point = recordSchema.safeParse(value).data;
  const lat = Number(point?.lat);
  const long = Number(point?.long);
  return Number.isFinite(lat) && Number.isFinite(long) ? { lat, long } : null;
}

function toPickerPage(
  raw: unknown,
  labelKey: string,
): { data: CourierPickerOption[]; count: number } {
  const body = recordSchema.safeParse(raw).data ?? {};
  const list = Array.isArray(body.data) ? body.data : [];
  const data = list.flatMap((item): CourierPickerOption[] => {
    const row = recordSchema.safeParse(item).data;
    const id = row?.id;
    if (typeof id !== 'string' && typeof id !== 'number') return [];
    return [{ id, label: text(row?.[labelKey]) || String(id) }];
  });
  return { data, count: numeric(body.count) || data.length };
}

async function getCourier(id: CourierProfileId) {
  return courierSchema.parse(
    unwrapData(await http.get<unknown>(`admin/couriers/${id}`, profileOptions)),
  );
}

export const courierProfileApi = {
  async listOrders(
    id: CourierProfileId,
    filter: CourierOrdersSearch,
  ): Promise<{ data: CourierOrderRow[]; count: number }> {
    const from = dayjs(filter.orderFrom ?? undefined).startOf('day');
    const till = dayjs(filter.orderTill ?? filter.orderFrom ?? undefined)
      .startOf('day')
      .add(1, 'day');
    const statusKey = {
      active: 'is_active',
      cancelled: 'is_cancelled_by_courier',
      finished: 'is_finished',
      returned: 'is_returned',
      're-offered': null,
    }[filter.orderStatus];
    const raw = await http.get<unknown>(
      filter.orderStatus === 're-offered' ? 'admin/orders/re-offered' : 'admin/orders',
      {
        ...profileOptions,
        query: {
          courier_id: id,
          from_date: from.toISOString(),
          till_date: till.toISOString(),
          limit: filter.orderPageSize,
          skip: (filter.orderPage - 1) * filter.orderPageSize,
          ...(statusKey ? { [statusKey]: true } : {}),
        },
      },
    );
    const body = pageBody(raw);
    const record = recordSchema.safeParse(body).data;
    const rows = Array.isArray(body) ? body : record?.data;
    const orders = z.array(recordSchema).parse(rows ?? []);
    return {
      data: orders.map((order) => mapOrder(order, filter.orderStatus)),
      count: numeric(record?.total_count ?? record?.count ?? orders.length),
    };
  },

  async getRatings(id: CourierProfileId, skip = 0, limit = 10) {
    const raw = await http.get<unknown>('admin/ratings', {
      ...profileOptions,
      query: { courier_id: id, skip, limit },
    });
    const page = z
      .object({
        data: z.array(courierRatingSchema).default([]),
        last_page: z.coerce.number().default(1),
      })
      .parse(pageBody(raw));
    return page;
  },

  async getTestResults(id: CourierProfileId) {
    const raw = await http.get<unknown>(`admin/couriers/${id}/test-results`, {
      baseURL: env.VITE_CORE_API_URL,
    });
    return z.array(courierTestResultSchema).parse(unwrapData(raw));
  },

  async getBlocks(
    id: CourierProfileId,
    skip = 0,
    limit = 10,
  ): Promise<{ data: z.infer<typeof courierBlockSchema>[]; count: number }> {
    const raw = await http.get<unknown>(`admin/couriers/${id}/block`, {
      ...profileOptions,
      query: { skip, limit },
    });
    const page = z
      .object({
        data: z.array(courierBlockSchema).default([]),
        count: z.coerce.number().default(0),
      })
      .parse(pageBody(raw));
    return page;
  },

  /** Legacy `updateCourier`: the personal details form writes to the admin courier. */
  async updatePersonalInfo(
    id: CourierProfileId,
    data: { first_name: string; last_name: string; phone: string },
  ): Promise<unknown> {
    return await http.put<unknown>(`admin/couriers/${id}`, data, {
      baseURL: env.VITE_CORE_API_URL,
    });
  },

  async updateRequirement(
    id: CourierProfileId,
    field: CourierRequirementField,
    value: boolean,
  ): Promise<unknown> {
    return await http.put<unknown>(
      `admin/couriers/${id}`,
      { [field]: value },
      { baseURL: env.VITE_CORE_API_URL },
    );
  },

  async updateEquipment(id: CourierProfileId, equipmentId: CourierProfileId) {
    return await http.put<unknown>(
      `admin/couriers/${id}`,
      { equipment_id: Number(equipmentId) },
      { baseURL: env.VITE_CORE_API_URL },
    );
  },

  /** `PUT admin/couriers/:id` — the personal-information Да/Нет answers. */
  async updatePersonalAnswers(
    id: CourierProfileId,
    answers: CourierPersonalAnswers,
  ): Promise<unknown> {
    return await http.put<unknown>(`admin/couriers/${id}`, answers, {
      baseURL: env.VITE_CORE_API_URL,
    });
  },

  /** `GET admin/scooters` — scooters not attached to a courier yet. */
  async listScooters(params: {
    search: string;
    skip: number;
    limit: number;
  }): Promise<{ data: CourierPickerOption[]; count: number }> {
    const raw = await http.get<unknown>('admin/scooters', {
      ...profileOptions,
      query: {
        limit: params.limit,
        skip: params.skip,
        is_attached: false,
        ...(params.search ? { display_id: params.search } : {}),
      },
    });
    return toPickerPage(raw, 'display_id');
  },

  /** `GET admin/batteries` — the battery picker, searchable by device id. */
  async listBatteries(params: {
    search: string;
    skip: number;
    limit: number;
  }): Promise<{ data: CourierPickerOption[]; count: number }> {
    const raw = await http.get<unknown>('admin/batteries', {
      ...profileOptions,
      query: {
        limit: params.limit,
        skip: params.skip,
        ...(params.search ? { dev_id: params.search } : {}),
      },
    });
    return toPickerPage(raw, 'dev_id');
  },

  /** `PATCH admin/scooters/:id/document` (multipart) — scooter + contract number + file. */
  async saveScooterDocument(input: {
    courierId: CourierProfileId;
    scooterId: CourierProfileId;
    contractNumber: string;
    contractFile: File | string | null;
  }): Promise<unknown> {
    const form = new FormData();
    form.append('scooter_id', String(input.scooterId));
    form.append('courier_id', String(input.courierId));
    form.append('contract_number', input.contractNumber);
    if (input.contractFile instanceof File)
      form.append('contract_file', input.contractFile);
    else if (typeof input.contractFile === 'string')
      form.append('contract_file', input.contractFile);
    return await http.patch<unknown>(
      `admin/scooters/${input.scooterId}/document`,
      form,
      profileOptions,
    );
  },

  /** `POST admin/batteries/:id/assign`. */
  async assignBattery(courierId: CourierProfileId, batteryId: CourierProfileId) {
    return await http.post<unknown>(
      `admin/batteries/${batteryId}/assign`,
      { courier_id: courierId },
      profileOptions,
    );
  },

  /** `DELETE admin/batteries/:id/remove`. */
  async removeBattery(courierId: CourierProfileId, batteryId: CourierProfileId) {
    return await http.delete<unknown>(`admin/batteries/${batteryId}/remove`, {
      ...profileOptions,
      body: { courier_id: courierId },
    });
  },

  /** Profile header + personal fields for the summary card and Profile tab. */
  async getById(id: CourierProfileId): Promise<CourierProfile> {
    const courier = await getCourier(id);
    const cash = recordSchema.parse(courier.courier_cash ?? {});
    return courierProfileSchema.parse({
      id: courier.id,
      fullName:
        [courier.first_name, courier.last_name, courier.middle_name]
          .map(text)
          .filter(Boolean)
          .join(' ') || `Courier#${id}`,
      courierNumber: text(courier.id),
      firstName: text(courier.first_name),
      lastName: text(courier.last_name),
      avatarUrl: text(courier.profile_photo) || null,
      phone: text(courier.phone),
      birthDate: text(courier.birth_date) || null,
      documentType: text(courier.doc_type) || null,
      birthCountry: text(courier.birth_country) || null,
      citizenship: text(courier.citizenship) || null,
      nationality: text(courier.nationality) || null,
      birthPlace: text(courier.birth_place) || null,
      pinfl: text(courier.pinfl) || null,
      issuedBy: text(courier.issued_by) || null,
      issuedDate: text(courier.issued_date) || null,
      expiryDate: text(courier.expiry_date) || null,
      passportSeries: text(courier.pass_data) || null,
      status: text(courier.status) || null,
      balance: numeric(courier.balance),
      scooterBalance: numeric(courier.scooter_balance),
      activeOrderId: Array.isArray(courier.active_orders)
        ? (courierSchema.safeParse(courier.active_orders[0]).data?.id ?? null)
        : null,
      appVersion: text(courier.app_version) || null,
      os: text(courier.os) || null,
      drivingLicenseFront: text(courier.driving_license_front_path) || null,
      drivingLicenseBack: text(courier.driving_license_back_path) || null,
      technicalPassportFront: null,
      technicalPassportBack: null,
      scooterDisplayId: text(courier.scooter_display_id) || null,
      scooterContractNumber: text(courier.scooter_contract_number) || null,
      scooterContractFile: text(courier.scooter_contract_file) || null,
      personalAnswers: {
        married: courier.married === true,
        has_children: courier.has_children === true,
        has_prev_exp: courier.has_prev_exp === true,
        has_driving_exp: courier.has_driving_exp === true,
        order_issues: courier.order_issues === true,
        considered_halal: courier.considered_halal === true,
        stress_issues: courier.stress_issues === true,
        bad_track_record: courier.bad_track_record === true,
        has_issues_with_loans: courier.has_issues_with_loans === true,
        relationship_issues: courier.relationship_issues === true,
        rating_comfirmation: courier.rating_comfirmation === true,
        has_map_exp: courier.has_map_exp === true,
      },
      scooterModel:
        text(
          courier.scooter_model ?? recordSchema.safeParse(courier.scooter).data?.model,
        ) || null,
      scooterBatteryId:
        typeof courier.battery_id === 'string' || typeof courier.battery_id === 'number'
          ? courier.battery_id
          : null,
      scooterBatteryPercent: null,
      scooterBatteryLocation: null,
      batteryPercentage:
        typeof courier.battery_percentage === 'number' ||
        typeof courier.battery_percentage === 'string'
          ? courier.battery_percentage
          : null,
      batteryLocation: mapPoint(courier.battery_location),
      location: mapPoint(courier.location),
      dealId:
        typeof courier.deal_id === 'string' || typeof courier.deal_id === 'number'
          ? courier.deal_id
          : null,
      dealAccepted: courier.deal_accepted === true,
      dealDate: text(courier.deal_date) || null,
      isOrderBlocked: courier.is_order_blocked === true,
      isIboxBlocked: courier.is_ibox_blocked === true,
      scooterId:
        typeof courier.scooter_id === 'string' || typeof courier.scooter_id === 'number'
          ? courier.scooter_id
          : null,
      equipmentId:
        typeof courier.equipment_id === 'string' ||
        typeof courier.equipment_id === 'number'
          ? courier.equipment_id
          : null,
      chatId:
        typeof courier.chat_id === 'string' || typeof courier.chat_id === 'number'
          ? courier.chat_id
          : null,
      requirements: {
        passed_test: courier.passed_test === true,
        visited_office: courier.visited_office === true,
        commissioner_is_set: courier.commissioner_is_set === true,
        driving_license_approved: courier.driving_license_approved === true,
        self_employment_status_set: courier.self_employment_status_set === true,
      },
      rating: numeric(courier.average_rating),
      // The reference profile does not expose these totals.
      ratingCount: null,
      cashBalance: numeric(cash.cash_balance),
      earned: null,
      transportType: text(courier.equipment_type),
      tags: [],
    });
  },

  async getLocation(id: CourierProfileId) {
    const raw = await http.get<unknown>(`admin/couriers/${id}/cache`, profileOptions);
    const body = unwrapData(raw);
    const payload = recordSchema.parse(body);
    return z
      .object({
        location: z.object({ lat: z.number(), long: z.number() }).nullish(),
        last_reported_at: z.string().nullish(),
        battery_id: z.union([z.string(), z.number()]).nullish(),
        battery_percentage: z.union([z.number(), z.string()]).nullish(),
      })
      .parse(payload);
  },

  async getBatteryLocation(batteryId: CourierProfileId, courierId: CourierProfileId) {
    const raw = await http.get<unknown>(`batteries/${batteryId}/status`, {
      ...profileOptions,
      query: { courier_id: courierId },
    });
    const values = z
      .array(z.object({ semaphore_id: z.string(), value: z.string() }).loose())
      .parse(unwrapData(raw));
    const read = (id: string) => values.find((item) => item.semaphore_id === id)?.value;
    const lat = Number(read('01103001'));
    const long = Number(read('01102001'));
    const percent = read('01113001');
    return {
      percentage: percent && Number.isFinite(Number(percent)) ? Number(percent) : null,
      location: Number.isFinite(lat) && Number.isFinite(long) ? { lat, long } : null,
    };
  },

  /** The courier's order history (Orders tab table). Response is a bare array. */
  async getOrders(id: CourierProfileId): Promise<CourierOrderRow[]> {
    const from = dayjs().startOf('day');
    const raw = await http.get<unknown>('admin/orders', {
      ...profileOptions,
      query: {
        courier_id: id,
        is_finished: true,
        limit: 10,
        skip: 0,
        from_date: from.toISOString(),
        till_date: from.add(1, 'day').toISOString(),
      },
    });
    return z
      .array(recordSchema)
      .parse(unwrapData(raw))
      .map((order) => mapOrder(order, 'finished'));
  },

  /** Contract form values + requirement switches (Contract tab). */
  async getContract(id: CourierProfileId): Promise<CourierContract> {
    const courier = await getCourier(id);
    return courierContractSchema.parse({
      documentNumber: text(courier.pass_data) || null,
      scooterModel: null,
      scooterNumber: text(courier.scooter_display_id) || null,
      photoUrl:
        typeof courier.scooter_contract_file === 'string'
          ? courier.scooter_contract_file
          : null,
      // These design switches have no equivalents in the reference page.
      requirements: {
        postpay: null,
        commission: null,
        directPay: null,
        compatibility: null,
      },
      lastSavedAt: null,
      savedSuccessfully: false,
    });
  },

  /** Issued equipment (Equipment tab grid). */
  async getEquipment(id: CourierProfileId): Promise<CourierEquipment> {
    const courier = await getCourier(id);
    const raw = await http.get<unknown>('admin/equipments', {
      ...profileOptions,
      query: { equipment_type: numeric(courier.courier_tariff_id) || 1 },
    });
    return courierEquipmentSchema.parse({
      items: z
        .array(recordSchema)
        .parse(unwrapData(raw))
        .map((item) => ({
          id: item.id,
          name: text(item.name_ru ?? item.name),
          issued: String(item.id) === String(courier.equipment_id),
          size: null,
        })),
    });
  },
};

/** Some endpoints wrap the payload under `data`; tolerate both. */
function meters(value: unknown): number | null {
  const parsed = Number(value);
  return value == null || !Number.isFinite(parsed) ? null : parsed / 1000;
}

function money(value: unknown): number | null {
  const parsed = Number(value);
  return value == null || !Number.isFinite(parsed) ? null : parsed;
}

function mapOrder(order: Record<string, unknown>, status: CourierOrderStatus) {
  const client = recordSchema.parse(order.client ?? {});
  const createdAt = text(order.created_at);
  const date = createdAt ? dayjs(createdAt) : null;
  return courierOrderRowSchema.parse({
    id: order.id,
    displayId: `#${text(order.display_id ?? order.id)}`,
    route: [
      ...z.array(z.string()).parse(order.origin_address ?? []),
      ...z.array(z.string()).parse(order.destination_address ?? []),
    ],
    deadHeadKm: meters(order.dead_head_distance),
    orderKm: meters(order.order_distance),
    status,
    price: money(order.total_delivery_price),
    paymentType: text(order.payment_type),
    client:
      [client.first_name, client.last_name].map(text).filter(Boolean).join(' ') ||
      text(client.name),
    productPrice: money(order.total_product_price),
    time: date?.isValid() ? date.format('HH:mm') : '',
    date: date?.isValid() ? date.format('YYYY-MM-DD') : '',
  });
}

function unwrapData(raw: unknown): unknown {
  if (raw && typeof raw === 'object' && 'data' in raw) {
    return raw.data;
  }
  return raw;
}

function pageBody(raw: unknown): unknown {
  if (
    raw &&
    typeof raw === 'object' &&
    'data' in raw &&
    raw.data &&
    typeof raw.data === 'object' &&
    !Array.isArray(raw.data)
  ) {
    return raw.data;
  }
  return raw;
}
